import { listActiveMemos, putMemo } from '../storage/memosStore.js';
import { renderMemoList } from './components/memo-list.js';
import { showActionSheet } from './components/action-sheet.js';
import { renderAiSummary } from './components/ai-summary.js';
import { api } from '../api/client.js';
import { getCachedSummary, setCachedSummary } from '../api/ai-cache.js';
import { showCelebration } from './components/toast.js';
import type { Memo } from '@memori-web/shared';

const TODAY_DATE = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export function renderTodayView(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="today-view">
      <div id="today-ai"></div>
      <div id="today-list"></div>
    </div>
  `;
  const aiEl = root.querySelector<HTMLDivElement>('#today-ai')!;
  const listEl = root.querySelector<HTMLDivElement>('#today-list')!;

  let cancelled = false;
  const refresh = async () => {
    const all = await listActiveMemos();
    const today = TODAY_DATE();
    const todayMemos: Memo[] = all.filter((m) => m.date === today);
    if (cancelled) return;

    // D1 が source of truth: mount 時に既存要約を取得 (失敗したら sessionStorage cache フォールバック)
    let initial = getCachedSummary(today);
    try {
      const fetched = await api.getSummary(today);
      if (fetched) {
        initial = fetched;
        setCachedSummary(today, fetched);
      }
    } catch {
      // network 失敗等は sessionStorage cache を使い続ける
    }
    if (cancelled) return;

    renderAiSummary(aiEl, today, initial, {
      onGenerate: async (date) => {
        const result = await api.summarize(date);
        setCachedSummary(date, result);
        showCelebration('要約ができたよ ✨');
        return result;
      },
    });

    renderMemoList(listEl, todayMemos, {
      onLongPress: async (m) => {
        await showActionSheet(`${m.timestamp} のメモ`, [
          {
            label: '📋 コピー',
            onSelect: () => navigator.clipboard.writeText(m.content),
          },
          {
            label: '📋 Markdown でコピー',
            onSelect: () => navigator.clipboard.writeText(`[${m.timestamp}] ${m.content}`),
          },
          {
            label: '🗑 削除',
            destructive: true,
            onSelect: async () => {
              const now = new Date().toISOString();
              await putMemo({ ...m, deletedAt: now, updatedAt: now }, { dirty: true });
              refresh();
              (window as unknown as { __triggerSync?: () => void }).__triggerSync?.();
            },
          },
        ]);
      },
    });
  };

  refresh();
  // 公開リフレッシュフック (Task 8 で compose 完了時に呼ぶ)
  (window as unknown as { __refreshToday?: () => void }).__refreshToday = refresh;

  return () => {
    cancelled = true;
    delete (window as unknown as { __refreshToday?: () => void }).__refreshToday;
  };
}
